import React, { useRef, useState } from "react";
import { Mic, Volume2, Loader2, Trash2, MessagesSquare } from "lucide-react";
import { api } from "@/lib/api";
import { getMicStream, micErrorMessage } from "@/lib/mic";
import { useI18n } from "@/context/I18nContext";
import { RecordingOverlay } from "@/components/VoiceRecorder";
import { toast } from "sonner";

function Turn({ turn }) {
  const [playing, setPlaying] = useState(false);
  const arSource = turn.sourceLang === "ar";

  const replay = async () => {
    setPlaying(true);
    try {
      const { data } = await api.post("/tts", { text: turn.translation, lang: turn.targetLang });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch {
      setPlaying(false);
    }
  };

  return (
    <div className={`flex ${arSource ? "justify-end" : "justify-start"}`} data-testid="conv-turn">
      <div
        className={`max-w-[85%] rounded-2xl p-3 shadow-sm ${
          arSource ? "bg-accent text-white rounded-ee-sm" : "bg-white border border-border rounded-ss-sm"
        }`}
      >
        <div className={`text-[11px] uppercase tracking-wider mb-1 ${arSource ? "text-white/70" : "text-muted-foreground"}`}>
          {turn.sourceLang === "ar" ? "AR" : "EN"} → {turn.targetLang === "ar" ? "AR" : "EN"}
        </div>
        <p className={`text-sm leading-relaxed ${arSource ? "text-white/80" : "text-muted-foreground"}`} dir={arSource ? "rtl" : "ltr"}>
          🎙 {turn.source_text}
        </p>
        <p className={`text-[15px] leading-relaxed font-medium mt-1.5 ${arSource ? "text-white" : "text-foreground"}`} dir={turn.targetLang === "ar" ? "rtl" : "ltr"}>
          {turn.translation}
        </p>
        <button
          onClick={replay}
          disabled={playing}
          data-testid="conv-replay-btn"
          className={`flex items-center gap-1 text-xs mt-2 pt-2 border-t ${arSource ? "border-white/20 text-white/90 hover:text-white" : "border-border text-primary"}`}
        >
          {playing ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} Replay
        </button>
      </div>
    </div>
  );
}

export function ConversationMode() {
  const { t, lang } = useI18n();
  const [turns, setTurns] = useState([]);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const endRef = useRef(null);

  const autoPlay = async (text, ttsLang) => {
    try {
      const { data } = await api.post("/tts", { text, lang: ttsLang });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.play();
    } catch (e) { console.error("Auto-play failed:", e); }
  };

  const sendAudio = async (blob) => {
    setBusy(true);
    const form = new FormData();
    form.append("file", blob, "conv.webm");
    form.append("target_lang", "auto"); // auto-detect language & translate to the other
    try {
      const { data } = await api.post("/voice/translate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTurns((prev) => [
        ...prev,
        {
          id: data.id,
          source_text: data.source_text,
          translation: data.translation,
          sourceLang: data.detected_language,
          targetLang: data.target_lang,
        },
      ]);
      autoPlay(data.translation, data.target_lang);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Voice translation failed");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await getMicStream();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((tk) => tk.stop());
        sendAudio(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(micErrorMessage(err, lang));
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="flex flex-col h-full" data-testid="conversation-mode">
      {(recording || busy) && (
        <RecordingOverlay mode={recording ? "recording" : "translating"} onStop={stopRecording} lang={lang} />
      )}

      {/* Auto-detect header */}
      <div className="bg-primary px-4 py-4 rounded-b-3xl text-center" data-testid="conv-header">
        <p className="text-white font-semibold text-sm">{t("convMode")}</p>
        <p className="text-white/80 text-xs mt-1 inline-flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-white/15">{t("autoDetect")}</span>
          العربية ⇄ English
        </p>
      </div>

      {/* Transcript */}
      <div className="flex-1 chat-pattern overflow-y-auto scroll-thin px-4 py-6 space-y-4">
        {turns.length === 0 ? (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-12">
            <div className="max-w-xs">
              <div className="h-16 w-16 rounded-2xl bg-white grid place-items-center mx-auto mb-4 shadow-sm">
                <MessagesSquare className="text-accent" size={28} />
              </div>
              <p className="text-sm">{t("convEmpty")}</p>
            </div>
          </div>
        ) : (
          turns.map((tn) => <Turn key={tn.id} turn={tn} />)
        )}
        <div ref={endRef} />
      </div>

      {/* Mic bar */}
      <div className="border-t border-border bg-white px-4 py-5 flex flex-col items-center gap-2">
        {turns.length > 0 && (
          <button
            onClick={() => setTurns([])}
            data-testid="conv-clear-btn"
            className="self-end -mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
          >
            <Trash2 size={13} /> {t("convReset")}
          </button>
        )}
        <button
          onClick={startRecording}
          disabled={busy || recording}
          data-testid="conv-mic-btn"
          className="h-20 w-20 rounded-full grid place-items-center text-white shadow-xl bg-accent hover:scale-105 transition-transform disabled:opacity-60"
        >
          <Mic size={30} />
        </button>
        <p className="text-sm text-muted-foreground" data-testid="conv-mic-hint">{t("convTapMic")}</p>
      </div>
    </div>
  );
}
